import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import PdfPreview from "../../../Common/PDFPreview"
import { RingLoader } from 'react-spinners';
import BackButton from "../../../Common/Buttons/BackButton";
import { formatReadableAmount } from "../../../Common/FormatFunctions/FormatAmount";

const API_URL = process.env.REACT_APP_API;

const StockReportDetail = () => {
  const location = useLocation();
  // const { fromDate, toDate, itemCode } = location.state;
  const companyCode = sessionStorage.getItem("Company_Code");
  const companyName = sessionStorage.getItem('Company_Name')

  const searchParams = new URLSearchParams(location.search);
  const itemCode = searchParams.get('itemCode');
  const godawncode = searchParams.get('godawnCode');
  const toDate = searchParams.get('toDate');
  const fromDate = searchParams.get('fromDate');

  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState([])
  const [showPreview, setShowPreview] = useState(false);

  const navigate = useNavigate();

  const groupBy = (array, key) => {
    return array.reduce((result, currentItem) => {
      const groupKey = currentItem[key];
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(currentItem);
      return result;
    }, {});
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1
    ).padStart(2, "0")}/${date.getFullYear()}`;
  };

  useEffect(() => {
    const fetchStockBookDetailReport = async () => {
      try {
        const response = await fetch(
          `${API_URL}/stock-book-detail?doc_date=${toDate}&company_code=${companyCode}&Item_Code=${itemCode}&godawn_Code=${godawncode}`
        );
        const result = await response.json();
        const filteredData = result.data.filter((item) => {
          const docDate = new Date(item.doc_date);
          const from = new Date(fromDate);
          const to = new Date(toDate);
          return docDate >= from && docDate <= to;
        });

        const grouped = filteredData.reduce((acc, item) => {
          const godown = item.godawnname || "";
          const itemName = item.System_Name_E || "";

          // Create Godown Group
          if (!acc[godown]) {
            acc[godown] = {};
          }

          // Create Item Group inside Godown
          if (!acc[godown][itemName]) {
            acc[godown][itemName] = [];
          }

          acc[godown][itemName].push(item);

          return acc;
        }, {});

        setGroupedData(grouped);
      } catch (error) {
        console.error("Error fetching stock book detail report:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStockBookDetailReport();
  }, [toDate, itemCode]);

  const handleBack = () => {
    navigate('/stock-book')
  }

  const generatePDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10; 

    const title = "Stock Book Detail Report";

    doc.setFontSize(14);
    doc.text(title, 10, 10);
    doc.setFontSize(10);
    doc.text(
      `From Date: ${formatDate(fromDate)} | To Date: ${formatDate(toDate)}`,
      10,
      20
    );

    let currentY = 30; 

    Object.keys(groupedData).forEach((group, groupIndex) => {
      if (currentY + 20 > pageHeight - margin) {
        doc.addPage();
        currentY = margin; 
      }

      doc.setFontSize(12);
      doc.setTextColor(40);
      doc.text(`Item Name: ${group.toUpperCase()}`, 10, currentY);
      currentY += 10;

      const tableData = groupedData[group].map((row) => [
        formatDate(row.doc_date),
        (parseFloat(row.opqntl) || 0).toFixed(2),
        (parseFloat(row.inwqntl) || 0).toFixed(2),
        (parseFloat(row.outqntl) || 0).toFixed(2),
        (parseFloat(row.bal) || 0).toFixed(2),
        (row.Marka),
        (row.Tran_Type),
        (row.DoNO),
        (row.doc_no),
        (row.partyShortname),
        (row.MillShortName)
      ]);

      doc.autoTable({
        startY: currentY,
        head: [
          [
            "Date",
            "Opening Qty",
            "Inward Qty",
            "Outward Qty",
            "Balance",
            "Marka",
            "Tran Type",
            "DO No",
            "Doc No",
            "Party Name",
            "Mill Short Name",

          ],
        ],
        body: [...tableData], 
        styles: { fontSize: 8, cellPadding: 3, halign: "right" },
        headStyles: { fillColor: [240, 240, 240], textColor: 0 },
        columnStyles: { 0: { halign: "center" } }, 
        didDrawPage: (data) => {
          currentY = data.cursor.y; 
        },
      });

      currentY = doc.lastAutoTable.finalY + 10;
    });

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setPdfPreview(pdfUrl);
    setShowPreview(true);
  };

  if (loading) return <div>{loading && (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <RingLoader />
    </div>
  )}
  </div>;

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <BackButton onClick={handleBack} />
      <div className="d-flex mb-3 mt-0.5">
        {showPreview && pdfPreview && (
          <PdfPreview pdfData={pdfPreview} apiData={groupedData} label={"Stock Book Detail Report"} />
        )}
        <button
          onClick={generatePDF}
          className="print-button"
          style={{ fontSize: "16px", height: "50px", marginTop: "10px" }}
        >
          PDF Preview
        </button>

      </div>
      <h2 style={{ textAlign: "center", marginBottom: "20px", color: "#333", marginTop: "-60px" }}>
        {companyName}
      </h2>
      <p style={{ textAlign: "center", fontSize: "14px", marginBottom: "20px" }}>
        <strong>Stock Book Detail Report From Date:</strong> {formatDate(fromDate)} |{" "}
        <strong>To Date:</strong> {formatDate(toDate)}
      </p>
     {Object.keys(groupedData).map((godown, index) => (
  <div key={index} style={{ marginBottom: "30px" }}>
    
    <h3
      style={{
        backgroundColor: "#f4f4f4",
        padding: "10px",
        textAlign: "left",
      }}
    >
      {godown.toUpperCase()}
    </h3>

    {Object.keys(groupedData[godown]).map((group, groupIndex) => (
      <div key={groupIndex} style={{ marginBottom: "25px" }}>

        <h4
          style={{
            backgroundColor: "#e8e8e8",
            padding: "8px",
            textAlign: "left",
          }}
        >
          ITEM : {group.toUpperCase()}
        </h4>

        <table
          border="1"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "center", padding: "10px" }}>Date</th>
              <th style={{ textAlign: "right", padding: "10px" }}>Opening Qty</th>
              <th style={{ textAlign: "right", padding: "10px" }}>Inward Qty</th>
              <th style={{ textAlign: "right", padding: "10px" }}>Outward Qty</th>
              <th style={{ textAlign: "right", padding: "10px" }}>Balance</th>
              <th style={{ textAlign: "center", padding: "10px" }}>Marka</th>
              <th style={{ textAlign: "center", padding: "10px" }}>Trans Type</th>
              <th style={{ textAlign: "center", padding: "10px" }}>DO</th>
              <th style={{ textAlign: "center", padding: "10px" }}>Doc No</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Party Name</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Mill Name</th>
            </tr>
          </thead>

          <tbody>
            {groupedData[godown][group].map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor:
                    rowIndex % 2 === 0 ? "#f9f9f9" : "white",
                }}
              >
                <td style={{ textAlign: "center" }}>
                  {formatDate(row.doc_date)}
                </td>

                <td style={{ textAlign: "right" }}>
                  {(parseFloat(row.opqntl) || 0).toFixed(2)}
                </td>

                <td style={{ textAlign: "right" }}>
                  {(parseFloat(row.inwqntl) || 0).toFixed(2)}
                </td>

                <td style={{ textAlign: "right" }}>
                  {(parseFloat(row.outqntl) || 0).toFixed(2)}
                </td>

                <td style={{ textAlign: "right" }}>
                  {(parseFloat(row.bal) || 0).toFixed(2)}
                </td>

                <td style={{ textAlign: "center" }}>
                  {row.Marka}
                </td>

                <td style={{ textAlign: "center" }}>
                  {row.Tran_Type}
                </td>

                <td style={{ textAlign: "center" }}>
                  {row.DoNO}
                </td>

                <td style={{ textAlign: "center" }}>
                  {row.doc_no}
                </td>

                <td style={{ textAlign: "left" }}>
                  {row.partyShortname}
                </td>

                <td style={{ textAlign: "left" }}>
                  {row.MillShortName}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot
                            style={{
                              backgroundColor: "#e0e0e0",
                              fontWeight: "bold",
                            }}
                          >
                            <tr>
                              <td style={{ textAlign: "center" }}>Total</td>
          
                              <td></td>
                              
          
                              <td style={{ textAlign: "right" }}>
                                {formatReadableAmount(
                                  groupedData[godown][group]
                                    .reduce(
                                      (sum, item) =>
                                        sum + (parseFloat(item.inwqntl) || 0),
                                      0
                                    )
                                    .toFixed(2)
                                )}
                              </td>
          
                              
          
                              <td style={{ textAlign: "right" }}>
                                {formatReadableAmount(
                                  groupedData[godown][group]
                                    .reduce(
                                      (sum, item) =>
                                        sum + (parseFloat(item.outqntl) || 0),
                                      0
                                    )
                                    .toFixed(2)
                                )}
                              </td>
          
                              <td style={{ textAlign: "right" }}>
                                {/* {formatReadableAmount(
                                  groupedData[godown][group]
                                    .reduce(
                                      (sum, item) =>
                                        sum + (parseFloat(item.bal) || 0),
                                      0
                                    )
                                    .toFixed(2)
                                )} */}
                              </td>
          
                              <td></td>
                              <td></td>
                              <td></td>
                               <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tfoot>
        </table>
      </div>
    ))}
  </div>
))}
    </div>
  );
};

export default StockReportDetail;




// import React, { useEffect, useState } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { jsPDF } from 'jspdf';
// import 'jspdf-autotable';
// import PdfPreview from "../../../Common/PDFPreview"
// import { RingLoader } from 'react-spinners';
// import BackButton from "../../../Common/Buttons/BackButton";

// const API_URL = process.env.REACT_APP_API;

// const StockReportDetail = () => {
//   const location = useLocation();
//   // const { fromDate, toDate, itemCode } = location.state;
//   const companyCode = sessionStorage.getItem("Company_Code");
//   const companyName = sessionStorage.getItem('Company_Name')

//   const searchParams = new URLSearchParams(location.search);
//   const itemCode = searchParams.get('itemCode');
//   const godawncode = searchParams.get('godawnCode');
//   const toDate = searchParams.get('toDate');
//   const fromDate = searchParams.get('fromDate');

//   const [groupedData, setGroupedData] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [pdfPreview, setPdfPreview] = useState([])
//   const [showPreview, setShowPreview] = useState(false);

//   const navigate = useNavigate();

//   const groupBy = (array, key) => {
//     return array.reduce((result, currentItem) => {
//       const groupKey = currentItem[key];
//       if (!result[groupKey]) {
//         result[groupKey] = [];
//       }
//       result[groupKey].push(currentItem);
//       return result;
//     }, {});
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return `${String(date.getDate()).padStart(2, "0")}/${String(
//       date.getMonth() + 1
//     ).padStart(2, "0")}/${date.getFullYear()}`;
//   };

//   useEffect(() => {
//     const fetchStockBookDetailReport = async () => {
//       try {
//         const response = await fetch(
//           `${API_URL}/stock-book-detail?doc_date=${toDate}&company_code=${companyCode}&Item_Code=${itemCode}&godawn_Code=${godawncode}`
//         );
//         const result = await response.json();
//         const filteredData = result.data.filter((item) => {
//           const docDate = new Date(item.doc_date);
//           const from = new Date(fromDate);
//           const to = new Date(toDate);
//           return docDate >= from && docDate <= to;
//         });

//         const grouped = filteredData.reduce((acc, item) => {
//           const godown = item.godawnname || "Unknown Godown";
//           const itemName = item.System_Name_E || "Unknown Item";

//           // Create Godown Group
//           if (!acc[godown]) {
//             acc[godown] = {};
//           }

//           // Create Item Group inside Godown
//           if (!acc[godown][itemName]) {
//             acc[godown][itemName] = [];
//           }

//           acc[godown][itemName].push(item);

//           return acc;
//         }, {});

//         setGroupedData(grouped);
//       } catch (error) {
//         console.error("Error fetching stock book detail report:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchStockBookDetailReport();
//   }, [toDate, itemCode]);

//   const handleBack = () => {
//     navigate('/stock-book')
//   }

//   const generatePDF = () => {
//     const doc = new jsPDF("landscape", "mm", "a4");
//     const pageHeight = doc.internal.pageSize.height;
//     const margin = 10; 

//     const title = "Stock Book Detail Report";

//     doc.setFontSize(14);
//     doc.text(title, 10, 10);
//     doc.setFontSize(10);
//     doc.text(
//       `From Date: ${formatDate(fromDate)} | To Date: ${formatDate(toDate)}`,
//       10,
//       20
//     );

//     let currentY = 30; 

//     Object.keys(groupedData).forEach((group, groupIndex) => {
//       if (currentY + 20 > pageHeight - margin) {
//         doc.addPage();
//         currentY = margin; 
//       }

//       doc.setFontSize(12);
//       doc.setTextColor(40);
//       doc.text(`Item Name: ${group.toUpperCase()}`, 10, currentY);
//       currentY += 10;

//       const tableData = groupedData[group].map((row) => [
//         formatDate(row.doc_date),
//         (parseFloat(row.opqntl) || 0).toFixed(2),
//         (parseFloat(row.inwqntl) || 0).toFixed(2),
//         (parseFloat(row.outqntl) || 0).toFixed(2),
//         (parseFloat(row.bal) || 0).toFixed(2),
//         (row.Marka),
//         (row.Tran_Type),
//         (row.DoNO),
//         (row.doc_no),
//         (row.partyShortname),
//         (row.MillShortName)
//       ]);

//       doc.autoTable({
//         startY: currentY,
//         head: [
//           [
//             "Date",
//             "Opening Qty",
//             "Inward Qty",
//             "Outward Qty",
//             "Balance",
//             "Marka",
//             "Tran Type",
//             "DO No",
//             "Doc No",
//             "Party Name",
//             "Mill Short Name",

//           ],
//         ],
//         body: [...tableData], 
//         styles: { fontSize: 8, cellPadding: 3, halign: "right" },
//         headStyles: { fillColor: [240, 240, 240], textColor: 0 },
//         columnStyles: { 0: { halign: "center" } }, 
//         didDrawPage: (data) => {
//           currentY = data.cursor.y; 
//         },
//       });

//       currentY = doc.lastAutoTable.finalY + 10;
//     });

//     const pdfBlob = doc.output("blob");
//     const pdfUrl = URL.createObjectURL(pdfBlob);
//     setPdfPreview(pdfUrl);
//     setShowPreview(true);
//   };

//   if (loading) return <div>{loading && (
//     <div
//       style={{
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         height: "100%",
//       }}
//     >
//       <RingLoader />
//     </div>
//   )}
//   </div>;

//   return (
//     <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
//       <BackButton onClick={handleBack} />
//       <div className="d-flex mb-3 mt-0.5">
//         {showPreview && pdfPreview && (
//           <PdfPreview pdfData={pdfPreview} apiData={groupedData} label={"Stock Book Detail Report"} />
//         )}
//         <button
//           onClick={generatePDF}
//           className="print-button"
//           style={{ fontSize: "16px", height: "50px", marginTop: "10px" }}
//         >
//           PDF Preview
//         </button>

//       </div>
//       <h2 style={{ textAlign: "center", marginBottom: "20px", color: "#333", marginTop: "-60px" }}>
//         {companyName}
//       </h2>
//       <p style={{ textAlign: "center", fontSize: "14px", marginBottom: "20px" }}>
//         <strong>Stock Book Detail Report From Date:</strong> {formatDate(fromDate)} |{" "}
//         <strong>To Date:</strong> {formatDate(toDate)}
//       </p>
//      {Object.keys(groupedData).map((godown, index) => (
//   <div key={index} style={{ marginBottom: "30px" }}>
    
//     <h3
//       style={{
//         backgroundColor: "#f4f4f4",
//         padding: "10px",
//         textAlign: "left",
//       }}
//     >
//       {godown.toUpperCase()}
//     </h3>

//     {Object.keys(groupedData[godown]).map((group, groupIndex) => (
//       <div key={groupIndex} style={{ marginBottom: "25px" }}>

//         <h4
//           style={{
//             backgroundColor: "#e8e8e8",
//             padding: "8px",
//             textAlign: "left",
//           }}
//         >
//           ITEM : {group.toUpperCase()}
//         </h4>

//         <table
//           border="1"
//           style={{
//             width: "100%",
//             borderCollapse: "collapse",
//             fontSize: "14px",
//           }}
//         >
//           <thead>
//             <tr>
//               <th style={{ textAlign: "center", padding: "10px" }}>Date</th>
//               <th style={{ textAlign: "right", padding: "10px" }}>Opening Qty</th>
//               <th style={{ textAlign: "right", padding: "10px" }}>Inward Qty</th>
//               <th style={{ textAlign: "right", padding: "10px" }}>Outward Qty</th>
//               <th style={{ textAlign: "right", padding: "10px" }}>Balance</th>
//               <th style={{ textAlign: "center", padding: "10px" }}>Marka</th>
//               <th style={{ textAlign: "center", padding: "10px" }}>Trans Type</th>
//               <th style={{ textAlign: "center", padding: "10px" }}>DO</th>
//               <th style={{ textAlign: "center", padding: "10px" }}>Doc No</th>
//               <th style={{ textAlign: "left", padding: "10px" }}>Party Name</th>
//               <th style={{ textAlign: "left", padding: "10px" }}>Mill Name</th>
//             </tr>
//           </thead>

//           <tbody>
//             {groupedData[godown][group].map((row, rowIndex) => (
//               <tr
//                 key={rowIndex}
//                 style={{
//                   backgroundColor:
//                     rowIndex % 2 === 0 ? "#f9f9f9" : "white",
//                 }}
//               >
//                 <td style={{ textAlign: "center" }}>
//                   {formatDate(row.doc_date)}
//                 </td>

//                 <td style={{ textAlign: "right" }}>
//                   {(parseFloat(row.opqntl) || 0).toFixed(2)}
//                 </td>

//                 <td style={{ textAlign: "right" }}>
//                   {(parseFloat(row.inwqntl) || 0).toFixed(2)}
//                 </td>

//                 <td style={{ textAlign: "right" }}>
//                   {(parseFloat(row.outqntl) || 0).toFixed(2)}
//                 </td>

//                 <td style={{ textAlign: "right" }}>
//                   {(parseFloat(row.bal) || 0).toFixed(2)}
//                 </td>

//                 <td style={{ textAlign: "center" }}>
//                   {row.Marka}
//                 </td>

//                 <td style={{ textAlign: "center" }}>
//                   {row.Tran_Type}
//                 </td>

//                 <td style={{ textAlign: "center" }}>
//                   {row.DoNO}
//                 </td>

//                 <td style={{ textAlign: "center" }}>
//                   {row.doc_no}
//                 </td>

//                 <td style={{ textAlign: "left" }}>
//                   {row.partyShortname}
//                 </td>

//                 <td style={{ textAlign: "left" }}>
//                   {row.MillShortName}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     ))}
//   </div>
// ))}
//     </div>
//   );
// };

// export default StockReportDetail;
